import type { Conversation } from "../../lib/conversations";

/**
 * The doorman's face, drawn rather than photographed.
 *
 * He is the one thing in this product that has an opinion before you have read
 * a word, which is why he is here at all: a list of knocks is a spreadsheet
 * until something in it is looking at you. Nine moods, one geometry — the head
 * never moves, the brows and lids and mouth do, exactly as the owner's
 * prototype poses them.
 *
 * WHAT WAS LEFT OUT OF THE PORT, and why. The prototype's face also carries six
 * metal finishes, eight cosmetics and a photo override, all of it wired to a
 * "Knob studio" its own settings panel marks as a later version and switches
 * off. None of that has a home in this IA, so none of it is here; the mood
 * system is the part that is load-bearing. The pointer-follow eyes are out too:
 * they are a window-level `pointermove` listener, which is scripted motion, and
 * the token layer's reduced-motion guard is CSS-only by design (see the note in
 * `styles/tokens.css` §5). A drawn face that does not chase the cursor is still
 * a drawn face.
 *
 * The colours are brand canon and live in the token layer with `ink`, for the
 * same reason: he is the same object in every theme.
 */
export type NobMood =
  "watching" | "interrogating" | "judging" | "rejected" | "open" | "bored" | "doubting" | "pleased";

/**
 * Which face a conversation wears.
 *
 * The prototype's ladder, with one clause moved: it tests the sender's standing
 * BEFORE the direction, and our outgoing rows carry no standing at all — a sent
 * knock's payload has a status and a label, never a band. So direction is asked
 * first for outgoing, which lands every one of them on `bored` — the same face
 * the prototype gives them, reached one step earlier.
 */
export function moodFor(conv: Conversation, doorLive: boolean): NobMood {
  if (conv.kind === "own-nob") return doorLive ? "open" : "watching";
  // A held spot was never interviewed, so the doorman has no opinion of it and
  // must not appear to (FR-G9.3). `watching` is the one face that is a posture
  // rather than a verdict; the ladder below is all verdicts, and `doubting` —
  // where a held spot would otherwise land, on a `standing` field it only
  // carries because the type demands one — would be a judgement nobody made.
  if (conv.stage === "pre-knock") return "watching";
  if (conv.stage === "turned-away") return "rejected";
  if (conv.stage === "through") return "pleased";
  if (conv.direction === "outgoing") return "bored";
  if (conv.standing === "needs-attention") return "judging";
  if (conv.standing === "held") return "doubting";
  return "interrogating";
}

const SIZE = {
  /** Beside a bubble (wave 90): 28px, and 24px on a phone so the text column keeps its width. */
  bubble: "h-6 w-6 sm:h-7 sm:w-7",
  sm: "h-8 w-8",
  md: "h-9 w-9",
  lg: "h-11 w-11",
  xl: "h-16 w-16",
  desk: "h-28 w-28",
} as const;

type Pose = { root?: string; lidL?: string; lidR?: string; browL?: string; browR?: string };

const POSE: Record<NobMood, Pose> = {
  watching: {
    lidL: "translate(0 3)",
    lidR: "translate(0 3.2)",
    browL: "translate(0 2) rotate(3 65 62)",
    browR: "translate(0 2) rotate(-2 117 66)",
  },
  interrogating: {
    lidL: "translate(0 -4)",
    browL: "translate(0 -8) rotate(-6 65 62)",
    browR: "translate(0 4) rotate(5 117 66)",
  },
  judging: {
    root: "rotate(-2 94 94)",
    lidL: "translate(0 6)",
    lidR: "translate(0 6)",
    browL: "translate(0 5) rotate(4 65 62)",
    browR: "translate(0 5) rotate(-3 117 66)",
  },
  rejected: {
    root: "rotate(-3 94 94)",
    lidL: "translate(0 8)",
    lidR: "translate(0 8)",
    browL: "translate(0 7) rotate(12 65 62)",
    browR: "translate(0 7) rotate(-12 117 66)",
  },
  open: {
    root: "rotate(1.5 94 94)",
    lidL: "translate(0 -13)",
    lidR: "translate(0 -13)",
    browL: "translate(0 -9) rotate(-4 65 62)",
    browR: "translate(0 -9) rotate(4 117 66)",
  },
  bored: {
    root: "rotate(1 94 94)",
    lidL: "translate(0 11)",
    lidR: "translate(0 11)",
    browL: "translate(0 9)",
    browR: "translate(0 9)",
  },
  doubting: {
    root: "rotate(-2 94 94)",
    lidL: "translate(0 4)",
    lidR: "translate(0 9)",
    browL: "translate(0 -9) rotate(-11 65 62)",
    browR: "translate(0 6) rotate(-4 117 66)",
  },
  pleased: {
    root: "rotate(1.5 94 94)",
    browL: "translate(0 -11) rotate(-5 65 62)",
    browR: "translate(0 -11) rotate(5 117 66)",
  },
};

/**
 * One mouth, chosen here rather than switched by nine CSS classes over seven
 * stacked paths. Same drawings, same moods, a tenth of the stylesheet.
 */
const MOUTH: Record<NobMood, string> = {
  watching: "M63 120 Q 84 119 106 122",
  interrogating: "M63 120 Q 84 124 106 117",
  judging: "M63 122 L106 122",
  rejected: "M63 125 Q 84 114 106 122",
  open: "M63 116 Q 85 134 107 115",
  bored: "M63 122 L106 122",
  doubting: "M63 120 Q 84 124 106 117",
  pleased: "M64 116 Q 85 136 106 114",
};

/** What each face is actually doing, for anyone who cannot see it. */
const MOOD_ALT: Record<NobMood, string> = {
  watching: "Nob, watching the door",
  interrogating: "Nob, one brow up, asking",
  judging: "Nob, looking again",
  rejected: "Nob, unimpressed",
  open: "Nob, holding the door open",
  bored: "Nob, waiting",
  doubting: "Nob, doubtful",
  pleased: "Nob, pleased",
};

/**
 * The paper grain, and the roughened outline that goes with it.
 *
 * These are the reference build's two filters, kept and pared down: a low
 * `feTurbulence` displaced into the geometry so the outline wobbles the way an
 * inked line does, and a high-frequency one stepped through a discrete alpha
 * ramp into scattered specks, flooded with the head's own darker orange. They
 * are rendered ONLY when a face is asked for `textured`, which in practice
 * means the poster surfaces — the sign-in, the onboarding runs, a door plate.
 *
 * The ids are per-instance. Two textured faces on one screen would otherwise
 * share one `<defs>` by duplicate id, which browsers resolve to "the first one
 * wins" — harmless while the filters are identical and a silent trap the day
 * they are not.
 */
function Grain({ id }: { id: string }) {
  return (
    <>
      <filter id={`${id}-rough`} x="-18%" y="-18%" width="146%" height="150%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.026"
          numOctaves="3"
          seed="5"
          result="w"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="w"
          scale="1.8"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
      <filter id={`${id}-spray`} x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.58"
          numOctaves="2"
          seed="11"
          result="n"
        />
        <feComponentTransfer in="n" result="speck">
          <feFuncA type="discrete" tableValues="0 0 0 0 0 0 0 .16 .48 .78 1" />
        </feComponentTransfer>
        <feComposite in="speck" in2="SourceGraphic" operator="in" result="dots" />
        <feFlood className="nob-knob-spray" result="c" />
        <feComposite in="c" in2="dots" operator="in" />
      </filter>
      <clipPath id={`${id}-head`}>
        <ellipse cx="94" cy="94" rx="78" ry="78" />
      </clipPath>
      <clipPath id={`${id}-plate`}>
        <ellipse cx="112" cy="112" rx="78" ry="78" />
      </clipPath>
    </>
  );
}

let textureSeq = 0;

export function NobFace({
  mood = "watching",
  size = "md",
  className = "",
  alt,
  textured = false,
}: {
  mood?: NobMood;
  size?: keyof typeof SIZE;
  className?: string;
  alt?: string;
  /** Poster surfaces only. See the note on `Grain` above. */
  textured?: boolean;
}) {
  const pose = POSE[mood];
  const gid = textured ? `nob-tex-${(textureSeq += 1)}` : "";
  const rough = textured ? `url(#${gid}-rough)` : undefined;
  return (
    <svg
      viewBox="-18 -28 232 244"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={alt ?? MOOD_ALT[mood]}
      data-mood={mood}
      data-size={size}
      data-textured={textured ? "true" : undefined}
      className={`nob-knob pointer-events-none block shrink-0 overflow-visible ${SIZE[size]} ${className}`}
    >
      <defs>
        {/* The lids slide behind the eye rather than over the face. */}
        <clipPath id="nob-eye-l">
          <path d="M40 74 C52 73 68 74 82 74 C82 90 75 99 62 100 C49 100 42 92 40 74 Z" />
        </clipPath>
        <clipPath id="nob-eye-r">
          <path d="M93 74 C107 74 124 74 139 74 C139 91 132 100 118 100 C104 100 95 92 93 74 Z" />
        </clipPath>
        {textured ? <Grain id={gid} /> : null}
      </defs>

      <g transform={pose.root}>
        {/* The offset plate behind him — a die-cut sticker, not a drop shadow. */}
        <g filter={rough}>
          <ellipse className="nob-knob-plate" cx="112" cy="112" rx="78" ry="78" />
          {textured ? (
            <g clipPath={`url(#${gid}-plate)`}>
              <rect
                className="nob-knob-shadow-grain"
                filter={`url(#${gid}-spray)`}
                x="30"
                y="30"
                width="170"
                height="170"
              />
            </g>
          ) : null}
        </g>

        <g filter={rough}>
          <path className="nob-knob-ear" d="M21 73 C -3 73 -4 105 20 107 C 15 97 15 83 21 73 Z" />
          <path
            className="nob-knob-ear"
            d="M168 71 C 199 67 207 111 172 117 C 181 103 181 85 168 71 Z"
          />
          <ellipse className="nob-knob-head" cx="94" cy="94" rx="78" ry="78" />
          {textured ? (
            <g clipPath={`url(#${gid}-head)`}>
              <rect
                className="nob-knob-grain"
                filter={`url(#${gid}-spray)`}
                x="13"
                y="13"
                width="164"
                height="164"
              />
              <ellipse className="nob-knob-edge-grain" cx="94" cy="94" rx="74" ry="74" />
            </g>
          ) : null}
        </g>

        <g>
          <path
            className="nob-knob-sclera"
            d="M40 74 C52 73 68 74 82 74 C82 90 75 99 62 100 C49 100 42 92 40 74 Z"
          />
          <g clipPath="url(#nob-eye-l)">
            <circle className="nob-knob-pupil" cx="48.6" cy="80.1" r="8.05" />
            <g transform={pose.lidL}>
              <path className="nob-knob-lid" d="M37 52 L85 52 L84 73 Q 62 75 39 74 Z" />
              <path className="nob-knob-lid-arc" d="M40 74 Q 61 75 82 74" />
            </g>
          </g>
          <path
            className="nob-knob-outline"
            d="M40 74 C52 73 68 74 82 74 C82 90 75 99 62 100 C49 100 42 92 40 74 Z"
          />
        </g>

        <g>
          <path
            className="nob-knob-sclera"
            d="M93 74 C107 74 124 74 139 74 C139 91 132 100 118 100 C104 100 95 92 93 74 Z"
          />
          <g clipPath="url(#nob-eye-r)">
            <circle className="nob-knob-pupil" cx="103.5" cy="80.3" r="8.05" />
            <g transform={pose.lidR}>
              <path className="nob-knob-lid" d="M90 52 L142 52 L141 73 Q 118 75 92 74 Z" />
              <path className="nob-knob-lid-arc" d="M93 74 Q 117 75 139 74" />
            </g>
          </g>
          <path
            className="nob-knob-outline"
            d="M93 74 C107 74 124 74 139 74 C139 91 132 100 118 100 C104 100 95 92 93 74 Z"
          />
        </g>

        <path className="nob-knob-brow" transform={pose.browL} d="M42 59 C55 56 65 66 88 65" />
        <path className="nob-knob-brow" transform={pose.browR} d="M96 66 C110 70 124 67 138 64" />

        <ellipse className="nob-knob-nose" cx="85.8" cy="97.3" rx="8.5" ry="8" />
        <path className="nob-knob-mouth" d={MOUTH[mood]} />
      </g>
    </svg>
  );
}

/**
 * The face on a conversation row.
 *
 * A knock that has been let through stops being a doorman's problem and starts
 * being a person, so it wears their face from then on — the cached picture if
 * the sender has one, the monogram if not; everything else is still the
 * doorman's, and wears his face. The prototype's rule, exactly.
 *
 * `person` is for the surfaces that are ABOUT the person whatever the stage —
 * the rail's sender card names them, counts their followers and links their
 * profile, and putting the doorman's face on that card says the wrong thing.
 * The list rows never pass it, so the rule above still owns the list.
 */
export function ConversationFace({
  conv,
  doorLive,
  size = "md",
  person = false,
}: {
  conv: Conversation;
  doorLive: boolean;
  size?: keyof typeof SIZE;
  person?: boolean;
}) {
  const isPerson = conv.kind !== "own-nob" && (person || conv.stage === "through");
  if (!isPerson) {
    return (
      <NobFace
        mood={moodFor(conv, doorLive)}
        size={size}
        alt={conv.kind === "own-nob" ? "Your Nob" : `${conv.name}'s Nob`}
      />
    );
  }
  return <PersonFace name={conv.name} handle={conv.handle} avatar={conv.avatar} size={size} />;
}

/**
 * A PERSON's face, at one of the shell's sizes: the cached picture, or the
 * monogram standing in for it.
 *
 * This is the branch `ConversationFace` has always ended on, lifted out and
 * given a name because two surfaces now need it for somebody who is not a
 * conversation at all — the settings button at the foot of the sidebar and the
 * card on the owner's own desk, both of which name the OWNER and used to wear
 * the doorman's face while doing it. The doorman is a product mark; those two
 * places are the reader's own reflection, and a reflection with somebody
 * else's face in it is the one thing an avatar must never be.
 *
 * There is deliberately no second implementation: `ConversationFace` routes
 * through here, so "picture, else monogram" is decided once for the shell.
 */
export function PersonFace({
  name,
  handle,
  avatar,
  size = "md",
}: {
  name: string;
  handle: string;
  avatar: string | null;
  size?: keyof typeof SIZE;
}) {
  if (avatar) {
    return (
      <img
        src={avatar}
        alt=""
        aria-hidden
        className={`shrink-0 rounded-full object-cover ${SIZE[size]}`}
      />
    );
  }
  return <Monogram name={name} handle={handle} size={size} />;
}

/** The one place a person, rather than a doorman, is standing. */
function Monogram({
  name,
  handle,
  size,
}: {
  name: string;
  handle: string;
  size: keyof typeof SIZE;
}) {
  const initial = (name.trim()[0] ?? handle.trim()[0] ?? "?").toUpperCase();
  return (
    <span
      aria-hidden
      className={`grid shrink-0 place-items-center rounded-full bg-raised-strong
                  text-sm font-semibold text-text ${SIZE[size]}`}
    >
      {initial}
    </span>
  );
}
